import { useEffect, useState } from 'react';
import {
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { FilterField, ListFiltersBar } from '../../components/ListFiltersBar';
import {
  Callout,
  ListEmptyState,
  LoadingSpinner,
  NumberInput,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { CompanyExpenseYearlyMonthlyReport } from '../../types/reports';
import { formatMoney } from '../../utils/format';
import { ReportExportButtons } from './ReportExportButtons';
import { exportCompanyExpenseYearlyMonthly } from './exportCompanyExpenseReports';

export function CompanyExpenseYearlyMonthlyPage() {
  const labels = t().reports;
  const [year, setYear] = useState(new Date().getFullYear());
  const [report, setReport] =
    useState<CompanyExpenseYearlyMonthlyReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getCompanyExpenseYearlyMonthlyReport(year);
        if (!cancelled) setReport(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [year]);

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          <ReportExportButtons
            disabled={!report}
            onExcel={() => {
              if (report)
                exportCompanyExpenseYearlyMonthly({ report, format: 'excel' });
            }}
            onPdf={() => {
              if (report)
                exportCompanyExpenseYearlyMonthly({ report, format: 'pdf' });
            }}
          />
        }
      >
        <FilterField label={labels.year}>
          <NumberInput
            value={year}
            allowDecimals={false}
            showClearButton={false}
            size="sm"
            onChange={(e) => {
              if (e.target.value != null) setYear(e.target.value);
            }}
          />
        </FilterField>
      </ListFiltersBar>

      {error ? (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      ) : null}

      <DataTablePanel>
        {loading ? (
          <LoadingSpinner message={t().common.loading} />
        ) : !report || report.totals.count === 0 ? (
          <ListEmptyState title={t().common.empty} />
        ) : (
          <table className="min-w-full text-sm">
            <thead className={dataTableHeadClass}>
              <tr>
                <th className="px-3 py-2 text-left font-medium">
                  {labels.month}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {labels.lineCount}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {labels.expenseNet}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {labels.expenseVat}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {labels.expenseGross}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t().financial.invoice}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {t().financial.cash}
                </th>
              </tr>
            </thead>
            <tbody>
              {report.months.map((row) => (
                <tr key={row.month} className="border-t border-line">
                  <td className="px-3 py-2">
                    {t().common.months[row.month - 1]}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {row.count}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(row.net)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(row.vat)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(row.gross)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(row.invoice)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(row.cash)}
                  </td>
                </tr>
              ))}
              <tr className="border-t border-line bg-surface-muted/40 font-semibold">
                <td className="px-3 py-2">{t().common.total}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {report.totals.count}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(report.totals.net)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(report.totals.vat)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(report.totals.gross)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(report.totals.invoice)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(report.totals.cash)}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </DataTablePanel>
    </PageShell>
  );
}
